import type { BeeModelManager } from "./BeeModelManager";
import type { BeeModel } from "./BeeModel";

import { find, filter, map, keyBy, sortBy, findIndex } from "@/utils/object";
import type { NumberString } from "@/store/api/dto/options";
import type { ModelDTO } from "@/store/api/dto/model.dto";

export class BeeModelCollection<
  T extends BeeModel = BeeModel,
  C extends object = object,
  O extends BeeModelManager["options"] = BeeModelManager["options"],
> {
  private modelsHash: Record<string, T> = {};
  private modelsByNameHash: Record<string, T> = {};

  private ModelClass: { new (manager: BeeModelManager, model: ModelDTO): T };
  public modelManager: BeeModelManager;
  private models: Array<T>;
  public options = {} as O;

  constructor(
    ModelClass: { new (manager: BeeModelManager, model: ModelDTO): T },
    modelManager: BeeModelManager,
    models: Array<T>,
    options = {} as O,
  ) {
    this.ModelClass = ModelClass;
    this.modelManager = modelManager;
    this.models = models;
    this.options = options;

    this.modelsHash = keyBy(models, "id");
    this.modelsByNameHash = keyBy(models, "name");
  }

  protected get classConstructor() {
    return this.constructor as typeof BeeModelCollection<T, C, O>;
  }

  get size() {
    return this.models.length;
  }

  createCollection(models: T[]): this {
    return new this.classConstructor(
      this.ModelClass,
      this.modelManager,
      models,
      this.options,
    ) as this;
  }

  first() {
    return this.all()[0] || null;
  }

  firstOrFail() {
    const record = this.first();
    if (record === null)
      throw new Error(
        `No records found in collection ${this.classConstructor.name}`,
      );
    return record;
  }

  all() {
    return this.models as T[];
  }

  map<X>(predicate: (v: T, index: number) => X) {
    return map(this.all(), predicate);
  }

  mapCollection(predicate: (model: T, index: number) => T) {
    const records = map(this.all(), predicate);
    return this.createCollection(records);
  }

  filter(predicate: (value: T, index: number) => boolean) {
    const records = filter(this.all(), predicate);
    return this.createCollection(records);
  }

  find(predicate: (model: T) => boolean) {
    return find(this.all(), predicate) ?? null;
  }

  findById(id?: NumberString): T | null {
    return this.modelsHash[String(id)] || null;
  }

  findIndex(id: NumberString) {
    return findIndex(this.all(), (m) => String(m.id) === String(id));
  }

  sortAsc(predicate: (model1: T, model2: T) => number) {
    const records = sortBy(this.all(), predicate) as T[];
    return this.createCollection(records);
  }

  sortDesc(predicate: (model1: T, model2: T) => number) {
    const records = sortBy(this.all(), predicate).reverse() as T[];
    return this.createCollection(records);
  }

  reverse() {
    const records = this.all().reverse();
    return this.createCollection(records);
  }

  get<T>(key: string) {
    return this.all().map((m) => m.get<T>(key)!);
  }

  whereIn(key: string, values: unknown[] = []) {
    const predicate =
      key === "id"
        ? (this.findById.bind(this) as (x: unknown) => T | null)
        : key === "name"
          ? (value: unknown) => this.modelsByNameHash[value as string]
          : (value: unknown) => this.all().find((x) => x.get(key)! === value);

    const records = values
      .map(predicate)
      .filter((x) => x !== undefined && x !== null);

    return this.createCollection(records);
  }
}
